import { useCallback, useState } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { KNOWLEDGE_BASES_QUERY_KEYS } from '@/entities/knowledge-base';
import { useAuth } from '@/entities/user';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import {
  createKnowledgeBase,
  deleteKnowledgeBase,
  FormError,
  KnowledgeBaseFormData,
  updateKnowledgeBase,
} from '@/features/knowledge-bases';
import {
  knowledgeBaseWorkspacesQuery,
  updateKnowledgeBaseWorkspaces,
} from '@/features/knowledge-bases/api/knowledgeBaseWorkspaces.api';
import { useKnowledgeBaseModal } from '@/features/knowledge-bases/model/knowledge-base-modal.store';
import { logger } from '@/lib/utils/logger';
import { notificationService } from '@/lib/services/notification.service';
import { diffWorkspaceScope } from '@/shared/lib/workspace-scope';
import { track } from '@/shared/analytics';
import { ROUTES } from '@/shared/config/routes';

/**
 * Hook for knowledge base actions (create, edit, delete)
 * Focused responsibility: API actions and navigation
 */
export const useKnowledgeBaseActions = ({
  canSelectWorkspaces = false,
}: { canSelectWorkspaces?: boolean } = {}) => {
  const { t } = useTranslation('knowledge');
  const navigate = useNavigate();
  const user = useAuth((state) => state.signedInUser.value);
  const currentWorkspaceId = useCurrentWorkspaceId();

  const {
    knowledgeBaseModalTypeCreate,
    knowledgeBaseModalTypeEdit,
    resetKnowledgeBaseModalState,
    setKnowledgeBaseModalLoading,
    knowledgeBaseModalLoading,
    deleteKnowledgeBaseModal,
  } = useKnowledgeBaseModal();

  const queryClient = useQueryClient();

  const [submitErrors, setSubmitErrors] = useState<FormError[]>([]);

  const handleCreateKnowledgeBase = useCallback(
    async (data: KnowledgeBaseFormData): Promise<void> => {
      if (!currentWorkspaceId) {
        throw new Error('Workspace information is missing');
      }

      setKnowledgeBaseModalLoading(true);
      try {
        const { id: knowledgeBaseId } = await createKnowledgeBase({
          name: data.title,
          description: data.description,
          ...(data.spaceVisibility === 'workspace' ? { workspaceId: currentWorkspaceId } : {}),
          ...(data.spaceVisibility === 'workspaces' ? { workspaceIds: data.workspaceIds } : {}),
          ...(data.spaceVisibility === 'internal' ? { isInternal: true } : {}),
        });

        track('kb_created', {
          kb_id: knowledgeBaseId,
          visibility: data.spaceVisibility === 'internal' ? 'internal' : 'workspace',
        });

        // Mark the list stale so the new entry shows when the user returns to it;
        // the global staleTime would otherwise hide it for up to 5 minutes.
        void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.all });

        void navigate(ROUTES.KNOWLEDGE_BASE_DETAIL(knowledgeBaseId));

        resetKnowledgeBaseModalState();
      } catch (error) {
        logger.error('Failed to create knowledge base:', error);

        setSubmitErrors([
          {
            field: 'general',
            severity: 'error',
            // eslint-disable-next-line i18next/no-literal-string -- dead field: lands in `submitErrors`, which no consumer reads
            title: error instanceof Error ? error.message : 'Failed to create knowledge base',
          },
        ]);

        throw error;
      } finally {
        setKnowledgeBaseModalLoading(false);
      }
    },
    [
      currentWorkspaceId,
      navigate,
      resetKnowledgeBaseModalState,
      setKnowledgeBaseModalLoading,
      queryClient,
    ],
  );

  /**
   * `add` and `remove` are the difference against what the API reports now, which is what leaves
   * a workspace this caller cannot see in neither list. Read after the details update, because
   * converting a foundation knowledge base is what makes it shareable at all.
   */
  const saveScopeIfChanged = useCallback(
    async (knowledgeBaseId: string, data: KnowledgeBaseFormData): Promise<void> => {
      // The query's own staleTime would hand back what the form loaded minutes ago, and a delta
      // computed against that misses what another admin changed since.
      const current = await queryClient.fetchQuery({
        ...knowledgeBaseWorkspacesQuery(knowledgeBaseId),
        staleTime: 0,
      });

      // The single-workspace choice, which only the surface without a picker still offers, means
      // just this workspace.
      const workspaceIds =
        data.spaceVisibility === 'workspace' && currentWorkspaceId
          ? [currentWorkspaceId]
          : data.workspaceIds;
      const delta = diffWorkspaceScope(
        current,
        {
          visibility: data.spaceVisibility === 'organization' ? 'organization' : 'workspaces',
          workspaceIds,
        },

        'organization',
      );
      if (!delta) return;

      const updated = await updateKnowledgeBaseWorkspaces(knowledgeBaseId, delta);

      queryClient.setQueryData(KNOWLEDGE_BASES_QUERY_KEYS.workspaces(knowledgeBaseId), updated);
    },
    [currentWorkspaceId, queryClient],
  );

  const handleEditKnowledgeBase = useCallback(
    async (
      data: KnowledgeBaseFormData,
      onSuccess?: (knowledgeBaseId: string) => void,
    ): Promise<void> => {
      if (!data.id) {
        throw new Error('Knowledge base ID is required for editing');
      }

      const newSpaceVisibility = data.spaceVisibility === 'workspace' ? currentWorkspaceId : null;
      const isInternal = data.spaceVisibility === 'internal';
      // With selection on, scope travels on its own endpoint and the details update leaves it alone.
      const scopeTravelsSeparately = canSelectWorkspaces && !isInternal;

      setKnowledgeBaseModalLoading(true);
      try {
        const { id: knowledgeBaseId } = await updateKnowledgeBase(data.id, {
          name: data.title,
          description: data.description,
          ...(scopeTravelsSeparately ? {} : { workspaceId: newSpaceVisibility ?? undefined }),
          isInternal,
        });

        if (scopeTravelsSeparately) {
          try {
            await saveScopeIfChanged(knowledgeBaseId, data);
          } catch (error) {
            // The details already landed, so the form stays open on the part that failed rather
            // than inviting a re-save of content that is stored.
            logger.error('Failed to update knowledge base sharing:', error);
            notificationService.showError(
              t('knowledgeBases.useKnowledgeBaseActions.sharingWasNotUpdated'),
              {
                description: t('knowledgeBases.useKnowledgeBaseActions.sharingWasNotUpdatedHint'),
              },
            );
            onSuccess?.(knowledgeBaseId);
            return;
          }
        }

        resetKnowledgeBaseModalState();

        // Call success callback with the updated knowledge base ID
        onSuccess?.(knowledgeBaseId);
      } catch (error) {
        logger.error('Failed to update knowledge base:', error);

        setSubmitErrors([
          {
            field: 'general',
            severity: 'error',
            // eslint-disable-next-line i18next/no-literal-string -- dead field: lands in `submitErrors`, which no consumer reads
            title: error instanceof Error ? error.message : 'Failed to update knowledge base',
          },
        ]);

        throw error;
      } finally {
        setKnowledgeBaseModalLoading(false);
      }
    },
    [
      resetKnowledgeBaseModalState,
      setKnowledgeBaseModalLoading,
      currentWorkspaceId,
      canSelectWorkspaces,
      saveScopeIfChanged,
      t,
    ],
  );

  const handleSubmit = useCallback(
    async (
      data: KnowledgeBaseFormData,
      onSuccess?: (knowledgeBaseId: string) => void,
    ): Promise<void> => {
      setSubmitErrors([]);

      if (knowledgeBaseModalTypeCreate) {
        await handleCreateKnowledgeBase(data);
      } else if (knowledgeBaseModalTypeEdit) {
        await handleEditKnowledgeBase(data, onSuccess);
      }
    },
    [
      knowledgeBaseModalTypeCreate,
      knowledgeBaseModalTypeEdit,
      handleCreateKnowledgeBase,
      handleEditKnowledgeBase,
    ],
  );

  const handleDelete = useCallback((): void => {
    deleteKnowledgeBaseModal();
  }, [deleteKnowledgeBaseModal]);

  const handleConfirmDelete = useCallback(
    async (data: KnowledgeBaseFormData): Promise<void> => {
      if (!data.id) {
        throw new Error('Knowledge base ID is missing');
      }

      if (!currentWorkspaceId) {
        throw new Error('Workspace information is missing');
      }

      setKnowledgeBaseModalLoading(true);
      try {
        await deleteKnowledgeBase(data.id);

        track('kb_deleted', { kb_id: data.id });

        // Mark the list stale so it refetches on remount; the global staleTime
        // would otherwise keep the deleted entry visible for up to 5 minutes.
        void queryClient.invalidateQueries({ queryKey: KNOWLEDGE_BASES_QUERY_KEYS.all });

        void navigate(ROUTES.KNOWLEDGE_BASES);

        resetKnowledgeBaseModalState();
      } catch (error) {
        logger.error('Failed to delete knowledge base:', error);

        setSubmitErrors([
          {
            field: 'general',
            severity: 'error',
            // eslint-disable-next-line i18next/no-literal-string -- dead field: lands in `submitErrors`, which no consumer reads
            title: error instanceof Error ? error.message : 'Failed to delete knowledge base',
          },
        ]);

        throw error;
      } finally {
        setKnowledgeBaseModalLoading(false);
      }
    },
    [
      currentWorkspaceId,
      navigate,
      resetKnowledgeBaseModalState,
      setKnowledgeBaseModalLoading,
      queryClient,
    ],
  );

  const clearSubmitErrors = useCallback(() => {
    setSubmitErrors([]);
  }, []);

  return {
    // Actions
    handleConfirmDelete,
    handleCreateKnowledgeBase,
    handleDelete,
    handleEditKnowledgeBase,
    handleSubmit,

    // State
    submitErrors,
    isSubmitting: knowledgeBaseModalLoading,
    clearSubmitErrors,

    // System checks
    hasOrganization: !!user?.organization.id,
    hasWorkspace: !!currentWorkspaceId,
  };
};
